import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  IconButton,
  Fade,
  Slide,
  Paper,
  Chip,
  CircularProgress,
} from "@mui/material";
import {
  CheckCircleOutline,
  ErrorOutline,
  Close,
} from "@mui/icons-material";
import { useDispatch } from "react-redux";
import { clearActiveRequests, updateLoading, updateLoadingMsg } from "../../Redux/Reducers/uiSlice";
import theme from "../../Theme/Theme"

const hideSpinner = (dispatch, onHide) => {
  dispatch(updateLoading(false));
  dispatch(clearActiveRequests());
  dispatch(updateLoadingMsg(""));
  onHide?.();
};

export const BottomRightLoader = ({ message, showMarketLoader = false, id = "default", onHide }) => {
  const dispatch = useDispatch();

  const handleHideSpinner = () => hideSpinner(dispatch, onHide);

  return (
    <Fade in={true} timeout={300}>
      <Slide direction="up" in={true} timeout={300}>
        <Box
          sx={{
            position: "fixed",
           
            bottom: 24,
            right: 24,
            zIndex: 9999,
            width: 320, // Fixed width for consistency
            minWidth: 320, // Ensure minimum width
          }}
        >
          <Paper
            elevation={8}
            sx={{
              borderRadius: 3,
          
              overflow: "hidden",
              background: (theme) =>
                theme.palette.mode === "light"
                  ? "linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)"
                  : "linear-gradient(135deg, #1565c0 0%, #1976d2 100%)",
              color: "white",
              position: "relative",
              "&::before": {
                content: '""',
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background: "rgba(255, 255, 255, 0.1)",
                // backdropFilter: "blur(10px)",
              },
            }}
          >
            {/* Header with status indicator */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                p: 2,
                pb: 1,
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1,     zoom: 0.75 }}>
                <Chip
                  label={showMarketLoader ? "Market Data" : "Processing"}
                  size="small"
                  sx={{
                    backgroundColor: "rgba(255, 255, 255, 0.2)",
                    color: ((theme) => theme.palette.mode === "dark" ? "white" : "black"),
                    fontSize: "0.7rem",
                    fontWeight: 600,
                    height: 20,
                  }}
                />
              </Box>
              <IconButton
                size="small"
                onClick={handleHideSpinner}
                sx={{
                  color: "white",
                  padding: "4px",
                  "&:hover": {
                    backgroundColor: "rgba(255, 255, 255, 0.1)",
                  },
                }}
              >
                <Close sx={{ fontSize: "16px" }} />
              </IconButton>
            </Box>

            {/* Main content */}
            <Box sx={{ p: 2, pt: 0 }}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 2,
                  mb: 1,
                }}
              >
                <Box
                  sx={{
                    position: "relative",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <CircularProgress
                    size={32}
                    thickness={4}
                    sx={{
                      color: "rgba(255, 255, 255, 0.3)",
                    }}
                  />
                  <CircularProgress
                    size={32}
                    thickness={4}
                    sx={{
                      color: "white",
                      position: "absolute",
                      animation: "spin 1s linear infinite",
                      "@keyframes spin": {
                        "0%": {
                          transform: "rotate(0deg)",
                        },
                        "100%": {
                          transform: "rotate(360deg)",
                        },
                      },
                    }}
                  />
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography
                    variant="body2"
                    sx={{
                      fontSize: "14px",
                      fontWeight: 600,
                      lineHeight: 1.3,
                      mb: 0.5,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {message || "Loading..."}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{
                      fontSize: "11px",
                      opacity: 0.8,
                      lineHeight: 1.2,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {showMarketLoader
                      ? "Fetching latest market data..."
                      : "Please wait while we process your request..."}
                  </Typography>
                </Box>
              </Box>
            </Box>

            {/* Progress bar */}
            <Box
              sx={{
                height: 3,
                background: "rgba(255, 255, 255, 0.2)",
                position: "relative",
                overflow: "hidden",
              }}
            >
              <Box
                sx={{
                  height: "100%",
                  background: "rgba(255, 255, 255, 0.6)",
                  width: "30%",
                  animation: "shimmer 2s ease-in-out infinite",
                  "@keyframes shimmer": {
                    "0%": {
                      transform: "translateX(-100%)",
                    },
                    "100%": {
                      transform: "translateX(400%)",
                    },
                  },
                }}
              />
            </Box>
          </Paper>
        </Box>
      </Slide>
    </Fade>
  );
};

// New component for managing multiple stacked loaders
export const StackedBottomRightLoaders = ({ loaders = [] }) => {
  const dispatch = useDispatch();

  return (
    <Box
      sx={{
        position: "fixed",
        bottom: 24,
        right: 24,
        zoom: 0.75,
        zIndex: 9999,
        display: "flex",
        flexDirection: "column",
        gap: 2,
        alignItems: "flex-end",
      }}
    >
      {loaders.map((loader, index) => (
        <Box
          key={loader.id || index}
          sx={{
            transform: `translateY(${index * -8}px)`,
            transition: "all 0.3s ease",
            "&:hover": {
              transform: `translateY(${index * -8 - 4}px)`,
            },
          }}
        >
          <Paper
            elevation={8 + index * 2}
            sx={{
              borderRadius: 3,
              overflow: "hidden",
              background: (theme) =>
                theme.palette.mode === "light"
                  ? "linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)"
                  : "linear-gradient(135deg, #1565c0 0%, #1976d2 100%)",
              color: "white",
              position: "relative",
              width: 320, // Fixed width for consistency
              minWidth: 320, // Ensure minimum width
              "&::before": {
                content: '""',
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background: "rgba(255, 255, 255, 0.1)",
              },
            }}
          >
            {/* Header with status indicator */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                p: 2,
                pb: 1,
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Chip
                  label={loader.showMarketLoader ? "Market Data" : "Processing"}
                  size="small"
                  sx={{
                    backgroundColor: "rgba(255, 255, 255, 0.2)",
                    color: ((theme) => theme.palette.mode === "dark" ? "white" : "black"),
                    fontSize: "0.7rem",
                    fontWeight: 600,
                    height: 20,
                  }}
                />
              </Box>
              <IconButton
                size="small"
                onClick={() => hideSpinner(dispatch, loader.onHide)}
                sx={{
                  color: "white",
                  padding: "4px",
                  "&:hover": {
                    backgroundColor: "rgba(255, 255, 255, 0.1)",
                  },
                }}
              >
                <Close sx={{ fontSize: "16px" }} />
              </IconButton>
            </Box>

            {/* Main content */}
            <Box sx={{ p: 2, pt: 0 }}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 2,
                  mb: 1,
                }}
              >
                <Box
                  sx={{
                    position: "relative",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <CircularProgress
                    size={32}
                    thickness={4}
                    sx={{
                      color: "rgba(255, 255, 255, 0.3)",
                    }}
                  />
                  <CircularProgress
                    size={32}
                    thickness={4}
                    sx={{
                      color: "white",
                      position: "absolute",
                      animation: "spin 1s linear infinite",
                      "@keyframes spin": {
                        "0%": {
                          transform: "rotate(0deg)",
                        },
                        "100%": {
                          transform: "rotate(360deg)",
                        },
                      },
                    }}
                  />
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography
                    variant="body2"
                    sx={{
                      fontSize: "14px",
                      fontWeight: 600,
                      lineHeight: 1.3,
                      mb: 0.5,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {loader.message || "Loading..."}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{
                      fontSize: "11px",
                      opacity: 0.8,
                      lineHeight: 1.2,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {loader.showMarketLoader
                      ? "Fetching latest market data..."
                      : "Please wait while we process your request..."}
                  </Typography>
                </Box>
              </Box>
            </Box>

            {/* Progress bar */}
            <Box
              sx={{
                height: 3,
                background: "rgba(255, 255, 255, 0.2)",
                position: "relative",
                overflow: "hidden",
              }}
            >
              <Box
                sx={{
                  height: "100%",
                  background: "rgba(255, 255, 255, 0.6)",
                  width: "30%",
                  animation: "shimmer 2s ease-in-out infinite",
                  "@keyframes shimmer": {
                    "0%": {
                      transform: "translateX(-100%)",
                    },
                    "100%": {
                      transform: "translateX(400%)",
                    },
                  },
                }}
              />
            </Box>
          </Paper>
        </Box>
      ))}
    </Box>
  );
};
